import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Spin, Table } from "antd";
import { Spinner } from "react-bootstrap";
import DateRangePicker from "react-bootstrap-daterangepicker";
import moment from "moment";
import "bootstrap-daterangepicker/daterangepicker.css";
import SidebarNav from "../sidebar";
import CustomPagination from "../Pagination/pagination";
import FilterPanel from "../commoncomponent/FilterPanel";
import TableToolbar from "../commoncomponent/TableToolbar";
import { useTableSettings } from "../../../hooks/useTableSettings";
import { sanitizeInput } from "../../../config/validation";
import {
  getInrDepositsApi,
  getInrWithdrawalsApi,
  exportInrDepositsCsvApi,
  exportInrWithdrawalsCsvApi,
} from "../../../service/api";
import {
  KpiCard,
  saveBlobAsFile,
  DATE_PRESETS,
  matchPresetKey,
} from "./shared";

const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Pending", value: "PENDING" },
  { label: "Approved", value: "ACCEPT" },
  { label: "Rejected", value: "REJECT" },
];

const formatINR = (n) => {
  const num = Number(n) || 0;
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDateTime = (val) =>
  val ? moment(val).format("DD/MM/YYYY HH:mm") : "-";

const STATUS_BADGE = {
  PENDING: { bg: "#fff4e5", color: "#fd7e14", label: "Pending" },
  ACCEPT: { bg: "#eaf7ee", color: "#28a745", label: "Approved" },
  REJECT: { bg: "#fdf0f1", color: "#dc3545", label: "Rejected" },
};
const renderStatus = (status) => {
  const s = STATUS_BADGE[status] || {
    bg: "#f1f5f9",
    color: "#6b7280",
    label: status || "-",
  };
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        borderRadius: 20,
        padding: "3px 10px",
        fontWeight: 600,
        fontSize: 12,
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  );
};

const ALL_COLUMNS = [
  {
    key: "user",
    title: "User",
    defaultVisible: true,
    render: (r) => (
      <div>
        <div style={{ fontWeight: 600, color: "#1a1a2e" }}>
          {r?.user?.firstName} {r?.user?.lastName}
        </div>
        <div style={{ fontSize: 12, color: "#8a94a6" }}>{r?.user?.email}</div>
      </div>
    ),
  },
  {
    key: "phoneNumber",
    title: "Phone",
    defaultVisible: false,
    render: (r) => r?.user?.phoneNumber || "-",
  },
  {
    key: "_id",
    title: "User _id",
    defaultVisible: false,
    render: (r) => (
      <span style={{ fontFamily: "monospace", fontSize: 12, color: "#374151" }}>
        {r?.user?._id || "-"}
      </span>
    ),
  },
  {
    key: "userId",
    title: "User ID",
    defaultVisible: false,
    render: (r) => (
      <span style={{ fontFamily: "monospace", fontSize: 12, color: "#374151" }}>
        {r?.user?.userId?.toUpperCase() || "-"}
      </span>
    ),
  },
  {
    key: "amount",
    title: "Amount",
    defaultVisible: true,
    align: "right",
    render: (r) => (
      <span style={{ fontWeight: 600, color: "#1b5a90" }}>
        {formatINR(r?.amount?.toFixed(2) || 0)}
      </span>
    ),
  },
  {
    key: "amountAfterDeduction",
    title: "Final Amount",
    defaultVisible: true,
    align: "right",
    render: (r) => (
      <span style={{ fontWeight: 700, color: "#28a745" }}>
        {formatINR(r?.amountAfterDeduction?.toFixed(2) || 0)}
      </span>
    ),
  },
  {
    key: "utrNumber",
    title: "UTR No.",
    defaultVisible: true,
    render: (r) => (
      <span style={{ fontFamily: "monospace", fontSize: 12, color: "#374151" }}>
        {r?.utrNumber || "-"}
      </span>
    ),
  },
  {
    key: "fee",
    title: "Fee",
    defaultVisible: false,
    align: "right",
    render: (r) => (
      <span style={{ color: "#6b7280" }}>
        {formatINR(r?.coinCdxFee?.toFixed(2) || 0)}
      </span>
    ),
  },
  {
    key: "gst",
    title: "GST",
    defaultVisible: false,
    align: "right",
    render: (r) => (
      <span style={{ color: "#6b7280" }}>{formatINR(r?.gst)}</span>
    ),
  },
  {
    key: "tds",
    title: "TDS",
    defaultVisible: false,
    align: "right",
    render: (r) => (
      <span style={{ color: "#6f42c1" }}>{formatINR(r?.tds)}</span>
    ),
  },
  {
    key: "status",
    title: "Status",
    defaultVisible: true,
    render: (r) => renderStatus(r?.status),
  },
  {
    key: "rejectReason",
    title: "Reject Reason",
    defaultVisible: false,
    render: (r) => (
      <span
        style={{
          fontSize: 12,
          color: "#dc3545",
          fontStyle: r?.rejectReason ? "normal" : "italic",
        }}
      >
        {r?.rejectReason || "-"}
      </span>
    ),
  },
  {
    key: "actionedBy",
    title: "Actioned By",
    defaultVisible: false,
    render: (r) => {
      const email =
        r?.status === "ACCEPT"
          ? r?.acceptedByEmail
          : r?.status === "REJECT"
            ? r?.rejectedByEmail
            : null;
      return email ? (
        <span style={{ fontSize: 12, color: "#374151" }}>{email}</span>
      ) : (
        <span style={{ color: "#ccc" }}>—</span>
      );
    },
  },
  {
    key: "createdAt",
    title: "Request Date",
    defaultVisible: true,
    render: (r) => (
      <span style={{ fontSize: 12, color: "#6b7280" }}>
        {formatDateTime(r?.createdAt)}
      </span>
    ),
  },
];

const INRTransactionsPage = ({ type }) => {
  const isDeposit = type === "DEPOSIT";
  const title = isDeposit ? "INR Deposit" : "INR Withdraw";
  const listApi = isDeposit ? getInrDepositsApi : getInrWithdrawalsApi;
  const exportApi = isDeposit
    ? exportInrDepositsCsvApi
    : exportInrWithdrawalsCsvApi;
  const storageKey = isDeposit
    ? "finance-inr-deposits"
    : "finance-inr-withdrawals";

  const [list, setList] = useState({ result: [], total: 0, summary: null });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  const {
    pageSize,
    setPageSize,
    pageSizeOptions,
    visibleColumnKeys,
    toggleColumn,
    resetColumns,
    dynamicColumns,
  } = useTableSettings({
    storageKey,
    allColumns: ALL_COLUMNS,
  });

  const params = useMemo(() => {
    const p = { page, sizePerPage: pageSize };
    if (searchTerm) p.search = searchTerm;
    if (status) p.status = status;
    if (startDate) p.startDate = startDate;
    if (endDate) p.endDate = endDate;
    return p;
  }, [page, pageSize, searchTerm, status, startDate, endDate]);

  const getList = () => {
    setLoading(true);
    listApi(params)
      .then((res) => {
        if (res?.status) {
          setList({
            result: res?.data?.result || [],
            total: res?.data?.total || 0,
            summary: res?.data?.summary || null,
          });
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, searchTerm, status, startDate, endDate]);

  useEffect(() => {
    setPage(1);
  }, [pageSize, searchTerm, status, startDate, endDate]);

  const handleDateRangeChange = (event, picker) => {
    setStartDate(picker.startDate.format("YYYY-MM-DD"));
    setEndDate(picker.endDate.format("YYYY-MM-DD"));
  };

  const activePreset = matchPresetKey(startDate, endDate);

  const applyPreset = (preset) => {
    if (preset === "all") {
      setStartDate();
      setEndDate();
      return;
    }
    const p = DATE_PRESETS.find((x) => x.key === preset);
    if (!p) return;
    const r = p.get();
    setStartDate(r.startDate);
    setEndDate(r.endDate);
  };

  const handleClear = () => {
    setSearchTerm("");
    setStatus("");
    setStartDate();
    setEndDate();
  };

  const handleExport = () => {
    setExporting(true);
    const exportParams = { ...params };
    delete exportParams.page;
    delete exportParams.sizePerPage;
    exportApi(exportParams)
      .then((res) => {
        const fname = `${isDeposit ? "inr-deposits" : "inr-withdrawals"}-${moment().format("YYYYMMDD")}.csv`;
        saveBlobAsFile(res, fname);
      })
      .finally(() => setExporting(false));
  };

  const summary = list.summary;
  const columns2 = [...dynamicColumns];

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header py-3 mb-2">
            <div className="row align-items-center">
              <div className="col-sm-12">
                <h3 className="page-title mb-1">{title}</h3>
                <ul className="breadcrumb mb-0" style={{ fontSize: 13 }}>
                  <li className="breadcrumb-item">
                    <Link to="/admin" className="text-decoration-none">
                      Dashboard
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Finance Dashboard / {title}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Quick date range tabs */}
          <div className="mb-3" style={{ width: "fit-content" }}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                background: "#f1f5f9",
                borderRadius: 12,
                padding: 5,
              }}
            >
              <button
                type="button"
                onClick={() => applyPreset("all")}
                style={{
                  padding: "7px 16px",
                  borderRadius: 8,
                  border: "none",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  background:
                    activePreset === "all" ? "#1b5a90" : "transparent",
                  color: activePreset === "all" ? "#fff" : "#6b7280",
                }}
              >
                All Time
              </button>
              {DATE_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => applyPreset(p.key)}
                  style={{
                    padding: "7px 16px",
                    borderRadius: 8,
                    border: "none",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    background:
                      activePreset === p.key ? "#1b5a90" : "transparent",
                    color: activePreset === p.key ? "#fff" : "#6b7280",
                  }}
                >
                  {p.label}
                </button>
              ))}
              {activePreset === "custom" && (
                <button
                  type="button"
                  style={{
                    padding: "7px 16px",
                    borderRadius: 8,
                    border: "none",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "default",
                    background: "#1b5a90",
                    color: "#fff",
                  }}
                >
                  {startDate} → {endDate}
                </button>
              )}
            </div>
          </div>

          <div className="list-layout">
            <div className="list-layout__main">
              {/* KPI cards */}
              <div className="row g-3 mb-3">
                {/* <div className="col-xl-3 col-sm-6 col-12">
                  <KpiCard
                    icon="∑"
                    label={`Total ${isDeposit ? "Deposits" : "Withdrawals"}`}
                    value={summary ? formatINR(summary.all.totalAmount) : "-"}
                    color="#1b5a90"
                    highlight
                  />
                </div> */}
                <div className="col-xl-3 col-sm-6 col-12">
                  <KpiCard
                    icon="A"
                    label={`Approved (${summary?.approved?.count ?? 0})`}
                    value={
                      summary ? formatINR(summary.approved.totalAmount) : "-"
                    }
                    color="#28a745"
                  />
                </div>
                <div className="col-xl-3 col-sm-6 col-12">
                  <KpiCard
                    icon="P"
                    label={`Pending (${summary?.pending?.count ?? 0})`}
                    value={
                      summary ? formatINR(summary.pending.totalAmount) : "-"
                    }
                    color="#fd7e14"
                  />
                </div>
                <div className="col-xl-3 col-sm-6 col-12">
                  <KpiCard
                    icon="R"
                    label={`Rejected (${summary?.rejected?.count ?? 0})`}
                    value={
                      summary ? formatINR(summary.rejected.totalAmount) : "-"
                    }
                    color="#dc3545"
                  />
                </div>
              </div>

              {/* Table */}
              <div
                className="card"
                style={{
                  borderRadius: 12,
                  border: "1px solid #e9ecef",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                }}
              >
                <div className="card-body">
                  <TableToolbar
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                    total={list.total}
                    page={page}
                    visibleColumnKeys={visibleColumnKeys}
                    allColumns={ALL_COLUMNS}
                    toggleColumn={toggleColumn}
                    resetColumns={resetColumns}
                    onRefresh={getList}
                    refreshLoading={loading}
                    rightSlot={
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleExport}
                        disabled={exporting}
                      >
                        {exporting ? <Spinner size="sm" /> : "Export CSV"}
                      </button>
                    }
                  />
                  <div className="table-responsive">
                    {loading ? (
                      <div className="his-spin">
                        <Spin
                          size="large"
                          style={{ position: "relative", top: "35%" }}
                        />
                      </div>
                    ) : (
                      <Table
                        pagination={false}
                        style={{ overflowX: "auto" }}
                        columns={columns2}
                        dataSource={list.result}
                        rowKey={(record) => record._id || record.id}
                      />
                    )}
                    {list.total > pageSize && (
                      <CustomPagination
                        key={`pg-${pageSize}`}
                        totalTabs={list.total}
                        tabsPerPage={pageSize}
                        handleChange={setPage}
                        className={loading ? "display-hide" : "display-flex"}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="list-layout__filters">
              <FilterPanel count={list.total} onClear={handleClear}>
                <FilterPanel.Field label="Search">
                  <input
                    type="text"
                    placeholder="Name, email, phone, address, tx hash"
                    value={searchTerm}
                    onChange={(e) =>
                      setSearchTerm(sanitizeInput(e.target.value))
                    }
                  />
                </FilterPanel.Field>
                <FilterPanel.Field label="Status">
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value || "all"} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </FilterPanel.Field>
                <FilterPanel.Field label="Date Range">
                  <DateRangePicker
                    onApply={handleDateRangeChange}
                    isClearable={true}
                  >
                    <input
                      type="text"
                      placeholder="Select date range"
                      value={
                        startDate && endDate ? `${startDate} → ${endDate}` : ""
                      }
                      readOnly
                    />
                  </DateRangePicker>
                </FilterPanel.Field>
              </FilterPanel>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default INRTransactionsPage;
